import {
  MOUNT_SELECTOR,
  MAX_PRODUCTS_COUNT_SELECTOR,
  PRODUCT_PER_PAGE_SELECTOR,
  PRODUCT_CARD_SECTION_ID,
  COLLECTION_EMPTY_STATE_SECTION_ID,
  COLLECTION_CAROUSEL_EMPTY_STATE_SECTION_ID,
  SORT_KEYS,
} from './constants.js';

function getVehicleSearchMounts() {
  return Array.from(document.querySelectorAll(MOUNT_SELECTOR));
}

function getProductCardsContainer(mount) {
  if (!mount) {
    return null;
  }

  if (isPredictiveSearchMount(mount)) {
    return mount.querySelector('[slot="results"] .predictive-search__products');
  }

  const carousel = mount.querySelector('scroll-carousel');
  return carousel ?? mount;
}

function getProductListMountHandle(mount) {
  if (!mount) {
    return null;
  }
  return mount.getAttribute('data-handle') ?? mount.getAttribute('handle') ?? null;
}

function getProductsPerPage(mount) {
  if (!mount) {
    return null;
  }
  return mount.getAttribute(PRODUCT_PER_PAGE_SELECTOR) ?? null;
}

function isProductRecommendationsMount(mount) {
  if (!mount) {
    return false;
  }
  return mount.getAttribute('data-product-recommendations') !== null;
}

function isPredictiveSearchMount(mount) {
  if (!mount) {
    return false;
  }
  return mount.getAttribute('data-predictive-search') !== null;
}

function isMainSearchMount(mount) {
  if (!mount) {
    return false;
  }
  return mount.hasAttribute('data-main-search');
}

function getMainSearchTerms(mount) {
  if (!mount) {
    return null;
  }
  const terms = mount.getAttribute('data-search-terms');
  return terms && terms.trim() !== '' ? terms.trim() : null;
}

function getProductRecommendationsProductId(mount) {
  if (!mount) {
    return null;
  }
  return mount.getAttribute('product') ?? null;
}

function getProductRecommendationsIntent(mount) {
  if (!mount) {
    return null;
  }
  return mount.getAttribute('intent') ?? null;
}

function getMaxProductsCount(mount) {
  if (!mount) {
    return null;
  }
  return mount.getAttribute(MAX_PRODUCTS_COUNT_SELECTOR) ?? null;
}

function getFiltersFromSearchParams(searchParams) {
  const filters = [];

  const price = {};

  searchParams.forEach((value, key) => {
    if (!key.startsWith('filter.')) return;

    if (key === 'filter.p.availability' || key === 'filter.v.availability') {
      filters.push({ available: value === '1' });
      return;
    }

    if (key === 'filter.p.price.gte' || key === 'filter.v.price.gte') {
      price.min = parseFloat(value);
      return;
    }
    if (key === 'filter.p.price.lte' || key === 'filter.v.price.lte') {
      price.max = parseFloat(value);
      return;
    }

    if (key === 'filter.p.product_type' || key === 'filter.v.product_type') {
      filters.push({ productType: value });
      return;
    }

    if (key === 'filter.p.vendor' || key === 'filter.v.vendor') {
      filters.push({ productVendor: value });
      return;
    }

    if (key === 'filter.p.tag' || key === 'filter.v.tag') {
      filters.push({ tag: value });
      return;
    }

    const variantOptionMatch = key.match(/^filter\.v\.option\.(.+)$/);
    if (variantOptionMatch) {
      filters.push({
        variantOption: {
          name: variantOptionMatch[1],
          value,
        },
      });
      return;
    }

    const productMetafieldMatch = key.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
    if (productMetafieldMatch) {
      filters.push({
        productMetafield: {
          namespace: productMetafieldMatch[1],
          key: productMetafieldMatch[2],
          value,
        },
      });
      return;
    }

    const variantMetafieldMatch = key.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
    if (variantMetafieldMatch) {
      filters.push({
        variantMetafield: {
          namespace: variantMetafieldMatch[1],
          key: variantMetafieldMatch[2],
          value,
        },
      });
      return;
    }
  });

  if (price.min !== undefined || price.max !== undefined) {
    filters.push({ price });
  }

  return filters;
}

function getPageFromSearchParams(searchParams) {
  const raw = searchParams.get('page');
  const pageNumber = parseInt(raw, 10);
  if (!Number.isFinite(pageNumber) || pageNumber < 1) {
    return 1;
  }
  return pageNumber;
}

function getItemSkus(item) {
  if (!item) {
    return [];
  }

  if (item.sku) {
    return [item.sku];
  }

  const variantEdges = item?.variants?.edges;

  if (Array.isArray(variantEdges)) {
    return variantEdges.map(({ node }) => node?.sku).filter(Boolean);
  }

  const variants = item?.variants;

  if (Array.isArray(variants)) {
    return variants.map((variant) => variant?.sku).filter(Boolean);
  }

  return [];
}

function isItemMatchingAllowedSkus(item, allowedSkus) {
  if (!allowedSkus?.size) {
    return true;
  }

  return getItemSkus(item).some((sku) => allowedSkus.has(sku));
}

function isItemProductTypeGeneric(item) {
  return item?.productType === 'Generic' || item?.productType === 'GenericMotorbike';
}

function filterItemsByAllowedSkus(items, allowedSkus) {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  if (!allowedSkus?.size) {
    return items;
  }

  return items.filter((item) => isItemMatchingAllowedSkus(item, allowedSkus) || isItemProductTypeGeneric(item));
}

async function renderProductListFragments(htmlFragments, mount, append = false, filters = '') {
  const productContainer = getProductCardsContainer(mount);
  if (!productContainer) {
    return;
  }

  const fragments = Array.isArray(htmlFragments) ? htmlFragments : [];

  if (fragments.length > 0) {
    if (append === 'append') {
      productContainer.insertAdjacentHTML('beforeend', fragments.join(''));
    } else if (append === 'prepend') {
      productContainer.insertAdjacentHTML('afterbegin', fragments.join(''));
    } else {
      productContainer.innerHTML = fragments.join('');
    }

    // Reinitialize iWish if it exists
    if (window.iWish) {
      window.iWish.init();
    }
    return;
  }

  if (!append) {
    await renderEmptyState(productContainer, mount, filters);
  }
}

async function renderEmptyState(productContainer, mount, filters = '') {
  if (isMainSearchMount(mount)) {
    productContainer.innerHTML = getNoResultsTemplate();
    return;
  }

  const handle = getProductListMountHandle(mount);
  if (!handle) {
    return;
  }

  const isCollection = productContainer.closest('.collection') !== null;

  const sectionId = isCollection ? COLLECTION_EMPTY_STATE_SECTION_ID : COLLECTION_CAROUSEL_EMPTY_STATE_SECTION_ID;

  const url = `collections/${handle}?section_id=${sectionId}${filters.length > 0 ? `&${filters}` : ''}`;
  const htmlString = await fetch(getUrlWithLanguage(url)).then((response) => response.text());

  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  const emptyRoot = doc.querySelector('.empty-state') ?? doc.querySelector('[id^="shopify-section"]');

  if (emptyRoot) {
    productContainer.innerHTML = emptyRoot.innerHTML;
  }
}

function getNoResultsTemplate(attributeName = 'data-no-results-template') {
  return document.querySelector(`[${attributeName}]`)?.getAttribute(attributeName) ?? '';
}

async function fetchProductCardHtml(handle) {
  const url = `products/${handle}?section_id=${PRODUCT_CARD_SECTION_ID}`;
  const response = await fetch(getUrlWithLanguage(url));
  const htmlString = await response.text();

  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  const section = doc.querySelector('.shopify-section--product-card');
  return section?.innerHTML ?? '';
}

function hideProductsCount() {
  document.querySelectorAll('.collection-toolbar__products-count')?.forEach((el) => {
    el.style.display = 'none';
  });
}

function showProductsCount() {
  document.querySelectorAll('.collection-toolbar__products-count')?.forEach((el) => {
    el.style.display = 'block';
  });
}

function getUrlWithLanguage(url) {
  url = url.startsWith('/') ? url.slice(1) : url;
  return `${Shopify.routes.root}${url}`;
}

function syncPaginationButtonsForMount(mount, { hasNextPage, hasPrevPage }) {
  if (!mount) {
    return;
  }
  const section = mount.closest('.shopify-section');
  if (!section) {
    return;
  }

  const nextButton = section.querySelector('.button[rel="next"]')?.closest('.pagination');
  if (nextButton) {
    if (!hasNextPage) {
      nextButton.style.display = 'none';
    } else {
      nextButton.style.display = 'block';
    }
  }

  const prevButton = section.querySelector('.button[rel="prev"]')?.closest('.pagination');
  if (prevButton) {
    if (!hasPrevPage) {
      prevButton.style.display = 'none';
    } else {
      prevButton.style.display = 'block';
    }
  }
}

let cachedLoaderMarkup = null;

function getVehicleSearchLoaderMarkup() {
  if (cachedLoaderMarkup == null) {
    cachedLoaderMarkup = document.getElementById('vehicle-search-loader-icon')?.innerHTML.trim() ?? '';
  }
  return cachedLoaderMarkup;
}

function setMountLoading(mount, isLoading, { append = false } = {}) {
  const cardsContainer = getProductCardsContainer(mount);
  if (!mount || !cardsContainer) {
    return;
  }

  if (isLoading) {
    if (mount.querySelector('.vehicle-search-mount__loader')) {
      return;
    }

    const markup = getVehicleSearchLoaderMarkup();
    if (!markup) {
      return;
    }

    if (!append) {
      cardsContainer.replaceChildren();
    }

    if (getComputedStyle(mount).position === 'static') {
      mount.style.position = 'relative';
    }

    mount.setAttribute('aria-busy', 'true');

    if (append) {
      mount.classList.add('vehicle-search-mount--append-loading');
    } else {
      mount.classList.add('vehicle-search-mount--loading');

      const loader = document.createElement('div');
      loader.className = 'vehicle-search-mount__loader';
      loader.setAttribute('role', 'status');
      loader.innerHTML = markup;
      mount.appendChild(loader);
    }

  } else {
    mount.removeAttribute('aria-busy');
    mount.classList.remove('vehicle-search-mount--loading', 'vehicle-search-mount--append-loading');
    mount.style.minHeight = '';
    mount.querySelector('.vehicle-search-mount__loader')?.remove();
  }
}

function setPaginationButtonsLoading(button, isLoading) {
  if (!button) {
    return;
  }

  if (isLoading) {
    button.setAttribute('disabled', '');
    button.style.pointerEvents = 'none';
    button.classList.add('is-loading');
  } else {
    button.removeAttribute('disabled');
    button.style.pointerEvents = '';
    button.classList.remove('is-loading');
  }
}

function restorePaginationButtons(mount) {
  if (!mount) {
    return;
  }

  const section = mount.closest('.shopify-section');
  if (!section) {
    return;
  }

  const prevButton = section.querySelector('.button[rel="prev"]');
  if (prevButton) {
    prevButton.style.display = 'none';
  }

  const nextButton = section.querySelector('.button[rel="next"]');

  if (nextButton) {
    nextButton.style.display = 'block';
    nextButton.href = nextButton.href.replace(/page=\d+/, 'page=2');
  }
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function toOptions(buckets = []) {
  return buckets.map((bucket) => ({
    id: String(bucket.key),
    label: String(bucket.key),
  }));
}

function toBuckets(values = []) {
  return unique(values).map((value) => ({
    key: value,
    doc_count: 1,
  }));
}

function normalizeVehicleCompatibility(item) {
  return {
    type: item.type === 'bici' ? 'bici' : 'moto',
    brand: String(item.brand ?? ''),
    model: String(item.modello ?? ''),
    year: String(item.anni ?? ''),
    yearFrom: String(item.annoDa ?? ''),
    yearTo: String(item.annoA ?? ''),
  };
}

function normalizeSortSelection(sortValue) {
  const graphqlSortKey = SORT_KEYS[sortValue] ?? SORT_KEYS['most-relevant'];

  if (graphqlSortKey.endsWith('_DESCENDING')) {
    return {
      sortKey: graphqlSortKey.replace('_DESCENDING', ''),
      reverse: true,
    };
  }

  if (graphqlSortKey.endsWith('_ASCENDING')) {
    return {
      sortKey: graphqlSortKey.replace('_ASCENDING', ''),
      reverse: false,
    };
  }

  return {
    sortKey: graphqlSortKey,
    reverse: false,
  };
}

function getSelectedSortKey() {
  const selectedSortValue = [...document.querySelectorAll('#sort-by-popover .popover__value-option')].find(
    (el) => el.getAttribute('aria-selected') === 'true',
  )?.value;

  if (selectedSortValue) {
    return normalizeSortSelection(selectedSortValue);
  }
  if (document.querySelector('[data-main-search]')) {
    return normalizeSortSelection('most-relevant');
  }
  return normalizeSortSelection('collection-default');
}

function resetPredictiveSearchMounts() {
  document.querySelectorAll('[data-predictive-search]').forEach((mount) => {
    const form = document.querySelector(`[aria-owns="${mount.id}"]`);
    const input = form?.elements?.q;
    if (input?.value?.trim()) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      mount.querySelector('[slot="results"]')?.replaceChildren();
    }
  });
}

export {
  getProductCardsContainer,
  getProductListMountHandle,
  getVehicleSearchMounts,
  getFiltersFromSearchParams,
  getPageFromSearchParams,
  getItemSkus,
  isItemMatchingAllowedSkus,
  isItemProductTypeGeneric,
  filterItemsByAllowedSkus,
  renderProductListFragments,
  fetchProductCardHtml,
  getProductsPerPage,
  getMaxProductsCount,
  hideProductsCount,
  showProductsCount,
  syncPaginationButtonsForMount,
  setPaginationButtonsLoading,
  setMountLoading,
  restorePaginationButtons,
  unique,
  toOptions,
  toBuckets,
  normalizeVehicleCompatibility,
  getSelectedSortKey,
  isProductRecommendationsMount,
  isMainSearchMount,
  getMainSearchTerms,
  getProductRecommendationsProductId,
  getProductRecommendationsIntent,
  resetPredictiveSearchMounts,
};
